import { baseApi, IPaginatedResponse, rewriteUrlIfDev } from '../../../apiHooks/baseApi';
import { IJob, JobState } from '@biolibtech/biolib-js';
import ModuleOutputV2 from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleOutputV2';
import { base64ToByteArray } from '@biolibtech/biolib-js/src/AppClient/utils';
import StdoutAndStderrPackage from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/StdoutAndStderrPackage';
import { dispatchMutation, dispatchQuery } from '../../../state';
import { utf8ToBase64 } from '../../../apiHooks/utils';
import { fetchWithRetries } from '../../../utils/fetchWithRetries';
import { IResultNote, ResultNoteType } from '../../results/types';
import { resultsApi } from '../../../apiHooks/resultApi';

export enum ResultStatus {
    pending = 'pending',
    completed = 'completed',
    error = 'error',
    cancelled = 'cancelled',
    unknown = 'unknown',
}

const jobStateToResultStatus: Record<string, ResultStatus> = {
    [JobState.inProgress]: ResultStatus.pending,
    [JobState.awaitingInput]: ResultStatus.pending,
    [JobState.completed]: ResultStatus.completed,
    [JobState.failed]: ResultStatus.error,
    [JobState.cancelled]: ResultStatus.cancelled,
};

const resultStatusToJobState: Record<string, string> = {
    [ResultStatus.pending]: JobState.inProgress,
    [ResultStatus.completed]: JobState.completed,
    [ResultStatus.error]: JobState.failed,
    [ResultStatus.cancelled]: JobState.cancelled,
};

export function mapJobStateToResultStatus(state: string): ResultStatus {
    const mapped = jobStateToResultStatus[state];
    if (!mapped) {
        console.error(`Unknown job state: ${state}`);
        return ResultStatus.unknown;
    }
    return mapped;
}

export function mapResultStatusToJobState(status: string): string {
    const mapped = resultStatusToJobState[status];
    if (!mapped) {
        console.error(`Unknown result status: ${status}`);
        return status;
    }
    return mapped;
}

export interface IFrameResultData {
    id: string;
    authToken: string;
    name: string;
    state: ResultStatus;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    exitCode: number | null;
    appUri: string;
}

interface IResultStorageResponse {
    job: IJob;
    presigned_download_url: string;
}

const iframeJobApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
        getResultStorageForIframe: builder.query<
            IResultStorageResponse,
            { resultId: string; authToken?: string }
        >({
            query: (params) => ({
                url: `/jobs/${params.resultId}/storage/results/download/`,
                headers: params.authToken ? { 'Job-Auth-Token': params.authToken } : undefined,
            }),
            // Min AWS presigned URL validity is 10 minutes
            keepUnusedDataFor: 480,
        }),
    }),
});

export async function getResult(requestPayload: Record<string, unknown>): Promise<IFrameResultData> {
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;

    const { job } = await dispatchQuery<IResultStorageResponse, { resultId: string; authToken?: string }>(
        iframeJobApi.endpoints.getResultStorageForIframe,
        { resultId, authToken },
        { forceRefetch: true },
    );
    return {
        id: job.uuid,
        authToken: job.auth_token,
        name: job.main_result?.name ?? '',
        state: mapJobStateToResultStatus(job.state),
        createdAt: job.created_at,
        startedAt: job.started_at,
        finishedAt: job.ended_at,
        exitCode: job.exit_code,
        appUri: job.app_uri,
    };
}

export interface IResultOutputFileInfo {
    path: string;
    sizeInBytes: number;
    resultId: string;
    resultAuthToken: string;
}

function normalizePath(path: string): string {
    return path.startsWith('/') ? path : `/${path}`;
}

export async function getResultOutputFileInfo(
    requestPayload: Record<string, unknown>,
): Promise<IResultOutputFileInfo> {
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;
    if (!authToken || typeof authToken !== 'string') {
        throw 'authToken is required and must be a string';
    }
    const path = requestPayload.path as string | undefined;
    if (!path || typeof path !== 'string') {
        throw 'path is required and must be a string';
    }

    const normalizedPath = normalizePath(path);
    const { presigned_download_url } = await dispatchQuery<IResultStorageResponse, { resultId: string; authToken: string }>(
        iframeJobApi.endpoints.getResultStorageForIframe,
        { resultId, authToken },
    );
    const presignedUrl = rewriteUrlIfDev(presigned_download_url);
    const response = await fetch(
        `${location.origin}/proxy/files/v1/get/?`
            .concat(`upstream_url_base64=${utf8ToBase64(presignedUrl)}`)
            .concat(`&path_base64=${utf8ToBase64(normalizedPath)}`),
    );
    if (!response.ok) {
        throw `File not found: ${response.status}`;
    }
    const fileNode = await response.json() as { full_path: string; size_in_bytes: number };
    return { path: fileNode.full_path, sizeInBytes: fileNode.size_in_bytes, resultId, resultAuthToken: authToken };
}

export async function getResultOutputFileData(
    requestPayload: Record<string, unknown>,
): Promise<Uint8Array> {
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;
    if (!authToken || typeof authToken !== 'string') {
        throw 'authToken is required and must be a string';
    }
    const path = requestPayload.path as string | undefined;
    if (!path || typeof path !== 'string') {
        throw 'path is required and must be a string';
    }

    const normalizedPath = normalizePath(path);
    const { presigned_download_url } = await dispatchQuery<IResultStorageResponse, { resultId: string; authToken: string }>(
        iframeJobApi.endpoints.getResultStorageForIframe,
        { resultId, authToken },
    );
    const presignedUrl = rewriteUrlIfDev(presigned_download_url);
    const response = await fetch(
        `${location.origin}/proxy/files/v1/data/?`
            .concat(`upstream_url_base64=${utf8ToBase64(presignedUrl)}`)
            .concat(`&path_base64=${utf8ToBase64(normalizedPath)}`),
    );
    if (!response.ok) {
        throw `Failed to fetch output file data: ${response.status}`;
    }
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
}

const textDecoder = new TextDecoder('utf-8', { fatal: true });

export async function streamResultLogs(
    requestPayload: Record<string, unknown>,
): Promise<{ logs: string; isCompleted: boolean; isStarted: boolean }> {
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;
    if (!authToken || typeof authToken !== 'string') {
        throw 'authToken is required and must be a string';
    }
    const full = (requestPayload.full as boolean | undefined) ?? true;

    const { job, presigned_download_url } = await dispatchQuery<
        IResultStorageResponse,
        { resultId: string; authToken: string }
    >(iframeJobApi.endpoints.getResultStorageForIframe, { resultId, authToken }, { forceRefetch: full });

    if (job.ended_at && full) {
        try {
            const moduleOutput = new ModuleOutputV2(rewriteUrlIfDev(presigned_download_url));
            const stdoutText = textDecoder.decode(await moduleOutput.getStdout());
            const stderrText = textDecoder.decode(await moduleOutput.getStderr());
            const logs = stdoutText.concat(stderrText ? `\n${stderrText}` : '');
            return { logs, isCompleted: true, isStarted: true };
        } catch {
            throw 'Failed to load logs from result storage';
        }
    }

    if (!job.cloud_job?.started_at) {
        return { logs: '', isCompleted: !!job.ended_at, isStarted: false };
    }

    const nodeUrl = rewriteUrlIfDev(job.cloud_job.compute_node_url);
    const url = `${nodeUrl}/v1/job/${job.public_id}/status/${full ? '?logs=full' : ''}`;
    const response = await fetchWithRetries(url);
    const result = (await response.json()) as {
        error_code?: number;
        is_completed: boolean;
        stdout_and_stderr_packages_b64: string[];
        streamed_logs_packages_b64?: string[];
    };

    const logPackages = result.streamed_logs_packages_b64?.length
        ? result.streamed_logs_packages_b64
        : (result.stdout_and_stderr_packages_b64 ?? []);

    const chunks: string[] = [];
    for (const packageB64 of logPackages) {
        const { stdout, stderr } = new StdoutAndStderrPackage(base64ToByteArray(packageB64)).getAttributes();
        if (stdout) {
            chunks.push(textDecoder.decode(stdout));
        }
        if (stderr) {
            chunks.push(textDecoder.decode(stderr));
        }
    }

    return {
        logs: chunks.join(''),
        isCompleted: result.is_completed || !!result.error_code,
        isStarted: true,
    };
}

function assertAllowedKeys(requestPayload: Record<string, unknown>, allowedKeys: string[]): void {
    const unexpectedKeys = Object.keys(requestPayload).filter((key) => !allowedKeys.includes(key));
    if (unexpectedKeys.length > 0) {
        throw `Unexpected keys: ${unexpectedKeys.join(', ')}`;
    }
}

export async function createResultNote(requestPayload: Record<string, unknown>): Promise<IResultNote> {
    assertAllowedKeys(requestPayload, ['resultId', 'authToken', 'note']);
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;
    if (!authToken || typeof authToken !== 'string') {
        throw 'authToken is required and must be a string';
    }
    const note = requestPayload.note as string | undefined;
    if (typeof note !== 'string' || note.length === 0) {
        throw 'note is required and must be a non-empty string';
    }

    return await dispatchMutation(resultsApi.endpoints.createNote, {
        jobUuid: resultId,
        jobAuthToken: authToken,
        note,
        type: ResultNoteType.internalEvent,
    });
}

export async function listResultNotes(
    requestPayload: Record<string, unknown>,
): Promise<IPaginatedResponse<IResultNote>> {
    assertAllowedKeys(requestPayload, ['resultId', 'authToken', 'page', 'page_size', 'types']);
    const resultId = requestPayload.resultId as string | undefined;
    if (!resultId || typeof resultId !== 'string') {
        throw 'resultId is required and must be a string';
    }
    const authToken = requestPayload.authToken as string | undefined;
    if (!authToken || typeof authToken !== 'string') {
        throw 'authToken is required and must be a string';
    }
    const page = requestPayload.page as number | undefined;
    const pageSize = requestPayload.page_size as number | undefined;
    const rawTypes = requestPayload.types;
    if (rawTypes !== undefined && (!Array.isArray(rawTypes) || rawTypes.some((type) => typeof type !== 'string'))) {
        throw 'types must be an array of strings';
    }
    const types = rawTypes as ResultNoteType[] | undefined;

    return await dispatchQuery<
        IPaginatedResponse<IResultNote>,
        { jobUuid: string; jobAuthToken: string; page?: number; pageSize?: number; types?: ResultNoteType[] }
    >(
        resultsApi.endpoints.getNotes,
        { jobUuid: resultId, jobAuthToken: authToken, page, pageSize, types },
        { forceRefetch: true },
    );
}
